import { useState } from "react";
import shortid from "shortid";
import * as XLSX from "xlsx";
import { Loading, Step, StepLabel, Stepper } from "../../components";
import { InputErros } from "../../helpers";
import { Minimal } from "../../layouts";
import {
  CestService,
  EnderecosEstoqueService,
  NcmService,
  notification,
  ProdutosService,
} from "../../services";
import { responseErros } from "../../utils";
import { ImportarProdutoActions, ImportarProdutoStepper } from "./components";
import CodigoBarraRepetidosDialog from "./components/dialogs/codigo-barras-repetidos.component";
import { Produtos } from "./entities";
import produtosCsvValidator from "./middlewares/produtos-csv.validator";
import CodigosFiscaisErrorDialog from "./components/dialogs/ncm-cest-erros.component";

const ImportarProdutoView = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [arquivoCsv, setArquivoCsv] = useState(null);
  const [dadosCsv, setDadosCsv] = useState(null);
  const [isCadastro, setIsCadastro] = useState(false);
  const [
    openProdutosCodigosRepetidosDialog,
    setOpenProdutosCodigosRepetidosDialog,
  ] = useState(false);
  const [produtosCodigosRepetidos, setProdutosCodigosRepetidos] = useState([]);
  const [produtosCodigosBarraRepetidos, setProdutosCodigosBarraRepetidos] = useState([]);
  const [ncmInexistentes, setNcmInexistentes] = useState([]);
  const [cestInexistentes, setCestInexistentes] = useState([]);
  const [openFiscalErroDialog, setOpenFiscalErroDialog] = useState(false);
  
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const produtosService = new ProdutosService();
  const ncmService = new NcmService();
  const cestService = new CestService();
  const enderecoEstoqueService = new EnderecosEstoqueService();

  const steps = [
    { titulo: "Selecionar Arquivo", funcaoAcao: buscarXmlData },
    { titulo: "Produtos", funcaoAcao: cadastrarProdutos },
  ];

  function extrairDadosProdutos(csvData) {
    const workbook = XLSX.read(csvData, { type: "buffer" });
    const planilhasValidas = ["PRODUTOS", "ANVISA", "COMBUSTIVEL"];
    let dataProdutos = [];
    for (const nomePlanilha of workbook.SheetNames) {
      if (!planilhasValidas.includes(nomePlanilha)) continue;
      const worksheet = workbook.Sheets[nomePlanilha];
      const data = XLSX.utils.sheet_to_json(worksheet);
      if (nomePlanilha === "PRODUTOS") {
        data.forEach((item) => {
          if (item.codigo == null || String(item.codigo).trim() === "") {
            throw new Error(
              "Há produtos sem código informado. Verifique a planilha antes de importar.",
            );
          }
          dataProdutos.push({
            ...item,
            id: shortid.generate(),
            ativado: true,
            tipo: "simples",
            cest: item.cest ? String(item.cest).padStart(7, "0") : null,
            fornecedores: [],
            produtosGrade: [],
          });
        });
        continue;
      }
      data.forEach((item, i) => {
        if (dataProdutos[i]) {
          dataProdutos[i] = { ...dataProdutos[i], ...item };
        }
      });
    }
    return dataProdutos?.length ? dataProdutos : [];
  }

  const handleFileRead = async (e) => {
    const csvData = e.target.result;
    const jsonData = extrairDadosProdutos(csvData);
    await formatarProdutos(jsonData);
    setIsCadastro(true);
  };

  async function buscarXmlData() {
    try {
      if (!arquivoCsv) {
        throw new Error(
          "Por favor, inserir CSV/XLS referente a importação de produtos",
        );
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          try {
            await handleFileRead(e);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () =>
          reject(new Error("Erro ao ler o arquivo CSV/XLS"));
        reader.readAsArrayBuffer(arquivoCsv);
      });
    } catch (error) {
      throw new Error(error.message);
    }
  }

  const validarCodigoBarras = async (dados = dadosCsv) => {
    const listCodigoBarras = dados
      .map((item) => item.codigoBarra)
      .filter((item) => typeof item === "string");
    if (new Set(listCodigoBarras).size !== listCodigoBarras.length) {
      const produtosRepetidos = dados.filter(
        (produto, index, self) =>
          self.findIndex((p) => p.codigoBarra === produto.codigoBarra) !==
          index,
      );
      setProdutosCodigosBarraRepetidos(produtosRepetidos);
      setOpenProdutosCodigosRepetidosDialog(true);
      return true;
    }
    return false;
  };

  async function cadastrarProdutos() {
    if (!dadosCsv?.length) {
      throw new Error(
        "Não há dados para importar. Por favor, verifique o arquivo CSV/XLS",
      );
    }
    produtosCsvValidator
      .validate(dadosCsv, { abortEarly: false })
      .then(async () => {
        setInputErro([]);
        setLoading(true);
        const codigoRepetido = await validarCodigoBarras();
        if (codigoRepetido) {
          setLoading(false);
          return;
        }
        const res = await produtosService.cadastrarListaProdutos(dadosCsv);
        setLoading(false);
        if (!res.isAxiosError) {
          notification.sucessoGenericos("Produtos importados com sucesso");
          handleInitStep();
        } else {
          if (
            res.response?.data?.message.includes("Existem códigos duplicados")
          ) {
            const errorResponse = res.response.data.response;
            const codigosProdutoExistentes = errorResponse.codigosProdutosExistentes?.map((codigo) => codigo.trim());
            const codigosBarraExistentes = errorResponse.codigosBarrasExistentes?.map((codigo) => codigo.trim());
            const produtosCodigoExistentes = dadosCsv.filter((produto) =>
              codigosProdutoExistentes.includes(String(produto.codigo)),
            );
            const produtosCodigoBarraExistentes = dadosCsv.filter((produto) =>
              codigosBarraExistentes.includes(produto.codigoBarra),
            );
            
            setProdutosCodigosRepetidos(produtosCodigoExistentes ?? [])
            setProdutosCodigosBarraRepetidos(produtosCodigoBarraExistentes ?? []);
            setOpenProdutosCodigosRepetidosDialog(true);
          } else {
            responseErros(res);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function formatarCodigosInexistentes(listaCompleta, listaExistentes) {
    const codigosInexistentes = [];
    listaCompleta.forEach((item) => {
      const existente = listaExistentes.find((ncm) => ncm.codigo === item);
      if (!existente) {
        codigosInexistentes.push(item);
      }
    });
    return codigosInexistentes;
  }

  async function formatarProdutos(dados) {
    setLoading(true);
    const ncmIdsProdutos = new Set();
    const cestIdsProdutos = new Set();
    const enderecosEstoque = new Set();
    dados.forEach((item) => {
      if (item.ncm) ncmIdsProdutos.add(String(item.ncm).trim());
      if (item.cest) cestIdsProdutos.add(String(item.cest).trim());
      if (item.enderecoEstoque)
        enderecosEstoque.add(item.enderecoEstoque.trim().toUpperCase());
    });
    const [listaNcm, listaCest, listaEnderecosEstoque] = await Promise.all([
      buscarNcms(Array.from(ncmIdsProdutos)),
      buscarCest(Array.from(cestIdsProdutos)),
      findOrCreateEnderecos(Array.from(enderecosEstoque)),
    ]);
    const cestsDistintos = new Set();
    if (listaCest?.length) {
      listaCest.forEach(cest => cestsDistintos.add(cest.codigo));
    }
    if (
      ncmIdsProdutos.size !== listaNcm.length ||
      cestIdsProdutos.size !== cestsDistintos.size
    ) {
      const ncms = formatarCodigosInexistentes(ncmIdsProdutos, listaNcm);
      const cests = formatarCodigosInexistentes(cestIdsProdutos, listaCest);
      setNcmInexistentes(ncms);
      setCestInexistentes(cests);
      setOpenFiscalErroDialog(true);
      throw new Error("Códigos fiscais inexistentes");
    }
    const produtos = dados.map((item) => ({
      ...new Produtos(item, listaNcm, listaCest, listaEnderecosEstoque),
      id: shortid.generate(),
    }));
    setDadosCsv(produtos);
  }

  const buscarNcms = async (codigos) => {
    const filtros = {
      codigo: codigos,
      nonPaginated: true,
    };
    if (codigos.length) {
      const result = await ncmService.getAllAvancado(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar NCMs",
        );
      }
    }
  };

  const buscarCest = async (codigos) => {
    const filtros = {
      codigo: codigos,
      nonPaginated: true,
    };
    if (codigos.length) {
      const result = await cestService.getAllAvancado(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar CESTs",
        );
      }
    }
  };

  async function findOrCreateEnderecos(enderecos) {
    if (enderecos.length) {
      const res = await enderecoEstoqueService.findOrCreateEnderecos({
        enderecos,
      });
      if (!res.isAxiosError) {
        return res.data;
      } else {
        throw new Error(
          res.response?.data?.message || "Erro ao buscar endereços de estoque",
        );
      }
    }
  }

  const handleInitStep = () => {
    setActiveStep(0);
    setArquivoCsv(null);
    setDadosCsv(null);
    setIsCadastro(false);
    setInputErro([]);
  };

  return (
    <>
      <Minimal
        title="Importação de produtos"
        cardAction={
          <ImportarProdutoActions
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleInitStep={handleInitStep}
            isCadastro={isCadastro}
            setIsCadastro={setIsCadastro}
            setLoading={setLoading}
          />
        }
      >
        <Stepper activeStep={activeStep}>
          {steps.map(({ titulo }) => (
            <Step key={titulo}>
              <StepLabel>{titulo}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ImportarProdutoStepper
          activeStep={activeStep}
          arquivoCsv={arquivoCsv}
          setArquivoCsv={setArquivoCsv}
          dadosCsv={dadosCsv}
          setIsCadastro={setIsCadastro}
          inputErros={inputErros}
        />
        <CodigoBarraRepetidosDialog
          open={openProdutosCodigosRepetidosDialog}
          setOpen={setOpenProdutosCodigosRepetidosDialog}
          produtosCodigosRepetidos={produtosCodigosRepetidos}
          produtosCodigosBarraRepetidos={produtosCodigosBarraRepetidos}
          handleInitStep={handleInitStep}
        />
        <CodigosFiscaisErrorDialog 
          open={openFiscalErroDialog}
          setOpen={setOpenFiscalErroDialog}
          ncmInexistentes={ncmInexistentes}
          cestInexistentes={cestInexistentes}
        />
      </Minimal>
      <Loading loading={loading} />
    </>
  );
};

export default ImportarProdutoView;
